import React, { useCallback } from 'react';
import { useClientTokenManager } from '@cko/dashboard-shared';
import { ToolkitFeatureFlag, Tooltip, TooltipFloatingDelayGroup } from '@cko/primitives';

import { BlockedLinkWrapper } from '@cko/dashboard-shared';
import { useNavigation } from '../../../providers';
import { AvailableMenuItem } from '../../../providers/NavigationProvider/types';
import { getMenuIcon, getMenuItemTestId } from '../../../utils';

import { Notifications } from '../Notifications';
import { NotificationsProvider } from '../Notifications/contexts';

import { SettingsMenuButton } from '../SettingsMenuButton';
import { TopBarInternalLink } from './TopBarInternalLink';
import { ActionContainer, TopBarExternalLink } from './Actions.styles';

export const Actions = () => {
  const {
    tokenProperties: { userType },
  } = useClientTokenManager();

  const { filteredMenuItems, getRouteById } = useNavigation();

  const actionItems = filteredMenuItems.actionItems || [];
  const getPath = useCallback(
    (menuItem: AvailableMenuItem): string | undefined => {
      if (menuItem.type === 'external_link') {
        return menuItem.url;
      }
      if (menuItem.type === 'internal_link') {
        return getRouteById(menuItem.routeId)?.path;
      }
    },
    [filteredMenuItems, getRouteById],
  );
  //TODO: intentional till we figure out the ordering issue post removal of the user menu
  const menuLinks = ['Developers', 'Notifications', 'Support'];

  const items = menuLinks.map((itemName: string) => {
    if (itemName === 'Notifications') {
      if (userType !== 'Internal') {
        return (
          <NotificationsProvider key={itemName}>
            <Notifications />
          </NotificationsProvider>
        );
      }
      return null;
    }
    const menuItem = actionItems.find((item: AvailableMenuItem) => {
      return item.label === itemName;
    });

    if (!menuItem) {
      return null;
    }

    const path = getPath(menuItem);
    const Icon = getMenuIcon(menuItem.iconName);
    const actionMenuItemTestId = getMenuItemTestId('action', menuItem.label);

    if (menuItem.type === 'external_link' || menuItem.type === 'internal_link') {
      return (
        <div>
          <ToolkitFeatureFlag flag="v10" key={itemName}>
            {menuItem.type === 'external_link' ? (
              <Tooltip text={menuItem.label}>
                <TopBarExternalLink target="_blank" href={path} data-testid={actionMenuItemTestId}>
                  {Icon && <Icon />}
                </TopBarExternalLink>
              </Tooltip>
            ) : (
              <Tooltip text={menuItem.label}>
                <BlockedLinkWrapper
                  linkType="primary"
                  to={menuItem.path}
                  requiredAccountLevels={menuItem.requiredAccountLevels}
                >
                  {({ isBlocked }) => (
                    <TopBarInternalLink
                      key={itemName}
                      to={path || ''}
                      isBlocked={isBlocked}
                      data-testid={actionMenuItemTestId}
                    >
                      {Icon && <Icon />}
                    </TopBarInternalLink>
                  )}
                </BlockedLinkWrapper>
              </Tooltip>
            )}
          </ToolkitFeatureFlag>
        </div>
      );
    }

    // Grouping types not supported currently so returning null for all other types.
    return null;
  });

  return (
    <TooltipFloatingDelayGroup>
      <ActionContainer>
        {items.map((item, index) => (
          <React.Fragment key={index}>{item}</React.Fragment>
        ))}
        <SettingsMenuButton />
      </ActionContainer>
    </TooltipFloatingDelayGroup>
  );
};
